import React, { useContext } from 'react';
import { MediumText, RegularText } from '../../lib/Text';
import { formatReportNumber, ReportMonthContext } from './sessionReportPresentation';

type Props = {
    prevRemaining: number;
    registeredSessions: number;
    progressed: number;
    handoffSessions: number;
    refundSessions: number;
    closed: number;
    /** 요약은 서버 계산값, 카드는 표시용 비교값. 이 컴포넌트는 합계를 계산하지 않는다. */
    remaining: number;
    handoffLabel?: string;
    comparison?: { remaining: number; diff: number };
    matched?: boolean;
    compact?: boolean;
    emphasizeRemaining?: boolean;
};

const signed = (value: number) => `${value < 0 ? '−' : '+'}${Math.abs(value).toLocaleString('ko-KR')}`;

/** 회원 요약과 수강권 카드에서 함께 쓰는 표시용 회차 흐름. */
export const SessionCountFlow = ({
    prevRemaining, registeredSessions, progressed, handoffSessions, refundSessions, closed, remaining,
    handoffLabel = '강사 간 넘김', comparison, matched = false, compact = false, emphasizeRemaining = false,
}: Props) => {
    const { month, at } = useContext(ReportMonthContext);
    const steps = [
        { label: '새로 판', value: registeredSessions, display: signed(registeredSessions) },
        { label: '수업', value: progressed, display: signed(-progressed) },
        { label: handoffLabel, value: handoffSessions, display: signed(handoffSessions) },
        { label: '환불', value: refundSessions, display: signed(refundSessions) },
        { label: '취소·정리', value: closed, display: signed(closed) },
    ];
    const visible = steps.filter((step) => step.value !== 0);
    // 기존 계산식 문장을 접근성 이름으로도 제공한다. 부호가 들어 있는 조정값은 그대로 더하는 값이다.
    const formula = `지난달 남은 ${prevRemaining} + 새로 판 ${registeredSessions} − ${month} 수업 ${progressed} + ${handoffLabel} (${handoffSessions}) + 환불 (${refundSessions}) + 취소·정리 (${closed}) = ${remaining}`;
    const box = (label: string, value: React.ReactNode, tone: 'neutral' | 'end' | 'warning' = 'neutral', prominent = false) => (
        <div className={`flex min-w-0 max-w-full flex-col justify-center gap-[5px] rounded-[10px] border border-solid ${compact ? 'min-h-[70px] px-[12px] py-[10px]' : 'min-h-[84px] px-[14px] py-[12px]'} ${
            tone === 'warning' ? 'border-[#FECACA] bg-[#FEF2F2]' : tone === 'end' ? 'border-[#D9E6FF] bg-[#EFF5FF]' : 'border-[#E5E8EB] bg-white'
        }`}>
            <RegularText fontSize={13} lineHeight={19} color={tone === 'warning' ? '#B91C1C' : '#4E5968'} className="break-keep">{label}</RegularText>
            <MediumText fontSize={prominent ? 30 : compact ? 18 : 22} lineHeight={prominent ? 38 : compact ? 25 : 30} color={tone === 'warning' ? '#B91C1C' : tone === 'end' ? '#2B5DB3' : '#3D4244'} className="tabular-nums break-all">
                {value}
            </MediumText>
        </div>
    );
    return (
        <div role="group" aria-label={formula} className="min-w-0 w-full">
            <ol aria-label="회차 흐름" className="flex flex-wrap items-stretch gap-x-[8px] gap-y-[10px]">
                <li className="flex min-w-0 max-w-full">{box('지난달 남은', formatReportNumber(prevRemaining))}</li>
                {visible.map((step) => (
                    <li key={step.label} className="flex min-w-0 max-w-full items-center gap-[8px]">
                        <span aria-hidden="true" className="shrink-0 text-[16px] text-[#4E5968]">→</span>
                        {box(step.label, step.display)}
                    </li>
                ))}
                <li className="flex min-w-0 max-w-full items-center gap-[8px]">
                    {!comparison && <span aria-hidden="true" className="shrink-0 text-[16px] text-[#4E5968]">→</span>}
                    <div className="flex min-w-0 flex-wrap items-center gap-[8px]">
                        {box(comparison ? '수업 기록으로 센 값' : '남은 회차', <>{matched && !comparison && remaining >= 0 && <span aria-label="계산이 맞아요" className="mr-[5px]">✓</span>}{formatReportNumber(remaining)}</>, comparison || remaining < 0 ? 'warning' : matched ? 'end' : 'neutral', emphasizeRemaining && !comparison)}
                        {comparison && <>
                            <span aria-label="같지 않음" className="text-[20px] text-[#B91C1C]">≠</span>
                            {box('수강권 기록', formatReportNumber(comparison.remaining), 'warning', emphasizeRemaining)}
                            <span className="rounded-[6px] bg-[#FEF2F2] px-[9px] py-[5px] text-[13px] leading-[19px] font-noto-medium text-[#B91C1C]">{`차이 ${formatReportNumber(comparison.diff)}회`}</span>
                        </>}
                    </div>
                </li>
            </ol>
            {!compact && visible.length < steps.length && <RegularText fontSize={13} lineHeight={19} color="#4E5968" className="mt-[8px] block">0인 항목은 뺐어요</RegularText>}
        </div>
    );
};
